import { Component, OnInit, AfterViewInit } from '@angular/core';
import { FormComponentLookup, FormPageComponentClass, FormBroadcast, FormNavigationConfig } from '@ptl/forms';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { plansNeedRefreshedFromBackend } from 'src/app/angular-elements/plan-selection/configs/reducers/plans.reducer';
import { MatSnackBar } from '@angular/material/snack-bar';
import moment, { Moment } from 'moment';
import { MomentTimeZoneEnum } from 'src/app/shared/enums/moment-timezone.enum';
import { environment } from 'src/environments/environment';
import { setIsDocumentUploadRequired } from 'src/app/angular-elements/shared/components/configs/reducers/life-event.reducer';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { GetOpenEnrollmentRule } from '../types/qualifying-life-event.response.interface';
import { deleteAllProxyValues, setProxyValue, saveSpecialEnrollmentCircumstanceState } from '../../plan-enrollment/individual-plan-enrollment-form/configs/reducers/special-enrollment-circumstance.reducer';
import { SpecialEnrollmentCircumstanceStateStructure } from '../../plan-enrollment/individual-plan-enrollment-form/configs/interfaces/special-enrollment-circumstance.StateStructure.interface';
import { submittedAttemptProxyConnectPath } from 'src/app/angular-elements/shared/interfaces/special-enrollment-circumstances.interface';
import { clearEffectiveDateAppState, setEffectiveDate } from 'src/app/angular-elements/shared/components/configs/reducers/effective-date.reducer';
import { clearIndividualMarketPayloadState } from '../../plan-enrollment/individual-plan-enrollment-form/configs/reducers/individual-market.reducer';

@FormComponentLookup('page-create-family-special-enrollment')
@Component({
  selector: 'app-page-create-family-special-enrollment',
  standalone: false,
  templateUrl: './page-create-family-special-enrollment.component.html',
  styleUrl: './page-create-family-special-enrollment.component.scss'
})
export class PageCreateFamilySpecialEnrollmentComponent extends FormPageComponentClass<FormNavigationConfig> implements OnInit, AfterViewInit {

  displaySpinner = false;
  customPageProperties;

  constructor(
    protected ngRedux: NgRedux<StateStructure>,
    private snackBar: MatSnackBar,
    private reducerUtilityService: ReducerUtilityService
  ) {
    super();
  }

  ngOnInit() {
    this.initPageConfig();
    this.customPageProperties = this.formConfig?.pages.length && this.formConfig.pages[this.currentPage]?.custom;
  }

  ngAfterViewInit(): void {
    this.resetFormData();
  }
  resetFormData() {}

  nextHook(formConfig): Promise<any> {
    return new Promise((resolve, reject) => {
      this.formPageActions.nextHook(formConfig);
      resolve(resolve);
    });
  }

  backHook(formConfig): Promise<any> {
    return new Promise((resolve, reject) => {
      this.formPageActions.backHook(formConfig);
      resolve(resolve);
    });
  }

  saveForLaterButtonClick(event: Event){
    if(event){
      this.saveForLaterButton();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  nextButtonClick(event: Event){
    if(event){
        this.nextButton();
      if(!this.formConfig.form.formNavigation?.nextButton?.redirect){
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  backButtonClick(event: Event){
    if(event){
      this.backButton();
      if(!this.formConfig.form.formNavigation?.backButton?.redirect){
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }
  
  submitButtonClick(event: Event){
    this.ngRedux.dispatch(setProxyValue(submittedAttemptProxyConnectPath, true));

    if(event){
      this.submitButton();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Upon successful validation and attempt to navigate
   * Trigger an event that the forms are listening to
   * This event will be consumed by the LIFE EVENT control,
   *   setting them.
   * 
   * The intent is that you visit this page, but only when you confirm submit will the life events be saved.
   * ( the dates will be saved though too... )
   */
  triggerFormStreamIsApplicableCircumstanceUpdate(applicableCircumstanceValue: string) {
    const evt: FormBroadcast = {
      formControlName: 'SXEANG_PROXY_isApplicableCircumstance_TRIGGER',
      type: 'valueChange',
      value: applicableCircumstanceValue
    };
    this.formStreamTrigger.triggerFormStream(evt);
  }

  triggerFormStreamLifeEventUpdate(eventValue: GetOpenEnrollmentRule) {

      const evt: FormBroadcast = {
        formControlName: 'SXEANG_PROXY_LIFE_EVENT_TRIGGER',
        type: 'valueChange',
        value: eventValue
      };
      this.formStreamTrigger.triggerFormStream(evt);
  }
  
  triggerFormStreamDateUpdate(date: Moment) {
    const evt: FormBroadcast = {
      formControlName: 'SXEANG_PROXY_LIFE_EVENT_DATE_TRIGGER',
      type: 'valueChange',
      value: date
    };
    this.formStreamTrigger.triggerFormStream(evt);
  }

  /**
   * Triggers 3 events, each in charge of setting the proxy component values ( so that the values are not added to state before solidified )
   * @param date Date of the Life Event
   */
  triggerFormStreamSideEffects(date: Moment, eventValue: GetOpenEnrollmentRule, applicableCircumstanceValue: string) {
    this.triggerFormStreamIsApplicableCircumstanceUpdate(applicableCircumstanceValue);
    this.triggerFormStreamLifeEventUpdate(eventValue);
    this.triggerFormStreamDateUpdate(date);
  }

  deleteProxyValues(){
  
    const applicableCircumstancePath = this.customPageProperties?.proxyApplicableCircumstanceConnectPath ?? 'PROXY:payloadStates:special-enrollment-circumstance:isApplicableCircumstance';
    const lifeEventPath = this.customPageProperties?.proxyLifeEventConnectPath ?? 'PROXY:payloadStates:special-enrollment-circumstance:lifeEvent';
    const dateOfLifeEventPath = this.customPageProperties?.proxyDateOfLifeEventConnectPath ?? 'PROXY:payloadstates:special-enrollment-circumstance:dateOfLifeEvent';

    const proxyValuePathArray = [submittedAttemptProxyConnectPath, applicableCircumstancePath, lifeEventPath, dateOfLifeEventPath];
    this.ngRedux.dispatch(deleteAllProxyValues(proxyValuePathArray));
  }

  assignAppValues(dateValue: Moment, eventValue: GetOpenEnrollmentRule, applicableCircumstanceValue: 'Yes' | 'No'){
    const specialEnrollmentStateValues: SpecialEnrollmentCircumstanceStateStructure = {
        dateOfLifeEvent: dateValue.toISOString(),
        isApplicableCircumstance: applicableCircumstanceValue,
        lifeEvent: eventValue
    };

    this.ngRedux.dispatch(saveSpecialEnrollmentCircumstanceState(specialEnrollmentStateValues));
  }

  clearOldAppValues(){
    this.ngRedux.dispatch(setEffectiveDate(null));
    this.ngRedux.dispatch(clearIndividualMarketPayloadState());
  }

  submitHook(): Promise<any> {
    return new Promise((resolve, reject) => {

      const payloadStates = this.reducerUtilityService.getPayloadStates();
      const specialEnrollmentCircumstance = payloadStates?.['special-enrollment-circumstance'];

      //get proxy values
      const eventValue = this.customPageProperties?.lifeEventObj ?? specialEnrollmentCircumstance?.[this.customPageProperties?.proxyLifeEventConnectPath];
      const applicableCircumstanceValue = specialEnrollmentCircumstance?.[this.customPageProperties?.proxyApplicableCircumstanceConnectPath] ?? "Yes";
      const date = specialEnrollmentCircumstance?.[this.customPageProperties?.proxyDateOfLifeEventConnectPath];

      // pass as value from event sideeffect
      if(this.customPageProperties?.isSinglePage){
         // 2025-08-01 : Current Issue, date is providing the wrong timezone
         this.assignAppValues(moment(date).tz(MomentTimeZoneEnum[environment.configs.siteTimeZone]), eventValue, applicableCircumstanceValue);
      }else{
        this.triggerFormStreamSideEffects(moment(date).tz(MomentTimeZoneEnum[environment.configs.siteTimeZone]), eventValue, applicableCircumstanceValue);
      }
      /** capture document upload info - this is a flag needed to be set/recorded/observed during producer-dashboard post-saved quotes experience */
      this.ngRedux.dispatch(setIsDocumentUploadRequired({
        isDocumentUploadRequired: this.customPageProperties.documentUploadRequirements?.minDocuments > 0,
        minRequired: this.customPageProperties.documentUploadRequirements?.minDocuments ?? 0
      }));

      // update state with refreshWatcherData
      this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
      if(specialEnrollmentCircumstance?.[this.customPageProperties?.proxyApplicableCircumstanceConnectPath] || specialEnrollmentCircumstance?.[this.customPageProperties?.proxyDateOfLifeEventConnectPath] || specialEnrollmentCircumstance?.[this.customPageProperties?.proxyLifeEventConnectPath]){
        this.clearOldAppValues();
      }
      this.deleteProxyValues();

      const urlRedirect = this.customPageProperties?.redirectUrl;

      const alertDuration = 1000; // ms

      if (urlRedirect) {

        this.snackBar.open(`Rate Information saved, proceeding to next page.`, 'OK', {
          duration: alertDuration,
          panelClass: 'positive-panel-class'
        });

        setTimeout(() => {
          window.location.assign(urlRedirect);
          resolve(true);
        }, alertDuration);

      }
    });
  }
}
